
// One free premium challenge per membership payment.
//
// A challenge is premium when its Stripe product has the metadata
// memberChallengeTier = "premium". A member's "payment" is the current
// billing period of their membership subscription (current_period_start
// to current_period_end). Free member registrations are the payment docs
// in stripe_customers/{uid}/payments with registerAsMember === true and
// added === true, so past claims are counted without any backfill.
//
// The new credit unlocks one day after the payment date
// (CREDIT_DELAY_MS), so a renewal that fails later that day can't be used
// to claim a premium challenge.
//
// Keep in sync with functions/premiumChallengeCredit.js.

export const CREDIT_DELAY_MS = 24 * 60 * 60 * 1000;
// Far enough back to cover the whole previous quarter.
const PREVIOUS_PERIOD_LOOKBACK_MS = 100 * 24 * 60 * 60 * 1000;

function toMs(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    const time = value.toDate().getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isPremiumMemberChallenge(productData) {
  const tier = productData?.metadata?.memberChallengeTier;
  return typeof tier === "string" && tier.trim().toLowerCase() === "premium";
}

export function getBillingPeriod(subscription) {
  const startMs = toMs(subscription?.current_period_start);
  const endMs = toMs(subscription?.current_period_end);
  if (!Number.isFinite(startMs)) return null;
  return {startMs, endMs: Number.isFinite(endMs) ? endMs : null};
}

// The time range whose premium claims block a new free premium claim.
// Normally the current billing period. During the first day after a
// payment it also covers the previous quarter.
export function getCreditWindow(subscription, nowMs = Date.now()) {
  const period = getBillingPeriod(subscription);
  if (!period) return null;
  const inFirstDay = nowMs < period.startMs + CREDIT_DELAY_MS;
  return {
    startMs: inFirstDay ?
      period.startMs - PREVIOUS_PERIOD_LOOKBACK_MS :
      period.startMs,
    endMs: period.endMs,
    periodStartMs: period.startMs,
  };
}

// Free member claims made inside the current billing period, oldest first.
// payments: [{id, registerAsMember, added, createdAt, metadata: {raceId}}]
export function getMemberClaimsInPeriod(payments, period, options = {}) {
  if (!period || !Array.isArray(payments)) return [];
  const {excludePaymentId = null, excludeRaceId = null} = options;

  return payments
    .filter((payment) => {
      if (!payment || payment.registerAsMember !== true) return false;
      if (payment.added !== true) return false;
      if (excludePaymentId && payment.id === excludePaymentId) return false;
      const raceId = payment.metadata?.raceId;
      if (!raceId || (excludeRaceId && raceId === excludeRaceId)) {
        return false;
      }
      const createdMs = toMs(payment.createdAt);
      if (!Number.isFinite(createdMs)) return false;
      if (createdMs < period.startMs) return false;
      return period.endMs === null || createdMs < period.endMs;
    })
    .sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt));
}

// productsById: {raceId: productData} for the races in the claims.
export function getPremiumCreditStatus({
  subscription,
  payments,
  productsById = {},
  excludePaymentId = null,
  excludeRaceId = null,
  nowMs = Date.now(),
}) {
  const period = getCreditWindow(subscription, nowMs);
  if (!period) {
    return {used: false, usedOn: null, nextCreditAtMs: null};
  }
  const nextPaymentCreditMs = Number.isFinite(period.endMs) ?
    period.endMs + CREDIT_DELAY_MS :
    null;

  const claims = getMemberClaimsInPeriod(payments, period, {
    excludePaymentId,
    excludeRaceId,
  });
  const premiumClaim = claims.find((payment) =>
    isPremiumMemberChallenge(productsById[payment.metadata.raceId])
  );

  if (!premiumClaim) {
    return {used: false, usedOn: null, nextCreditAtMs: nextPaymentCreditMs};
  }

  const claimedAtMs = toMs(premiumClaim.createdAt);
  return {
    used: true,
    usedOn: {
      raceId: premiumClaim.metadata.raceId,
      raceName: premiumClaim.metadata.raceName ||
        productsById[premiumClaim.metadata.raceId]?.name || "",
      claimedAtMs,
    },
    // Claimed with the previous payment: unlocks one day after this one.
    nextCreditAtMs: claimedAtMs < period.periodStartMs ?
      period.periodStartMs + CREDIT_DELAY_MS :
      nextPaymentCreditMs,
  };
}

